<script setup lang="ts">
import { Head, router, useForm } from '@inertiajs/vue3'
import { computed, onMounted, reactive } from 'vue'

import AddressForm from '@/components/AddressForm.vue'
import InputError from '@/components/InputError.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useRecaptcha } from '@/composables/useRecaptcha'
import LandingLayout from '@/layouts/LandingLayout.vue'
import { formatLabel } from '@/lib/utils'
import { applyPageCreateApplicationPath, applyPagePath } from '@/routes'

interface Address {
  street_no: string; street_name: string; street_type: string
  unit_no: string; city: string; state: string; postcode: string
  years_at_address: string; months_at_address: string; address_type: string
}

interface Prefill {
  loan_type?: string
  purpose?: string
  amount?: string
}

const props = defineProps<{
  customer_id: number
  customer_name?: string | null
  loan_terms: number[]
  purposes: Record<string, string[]>
  loan_types: string[]
  marital_statuses: string[]
  living_statuses: string[]
  existing_addresses: Address[]
  prefill?: Prefill
  recaptcha_site_key?: string | null
}>()

onMounted(() => {
  localStorage.setItem('tlc_customer_id', String(props.customer_id))
})

const { getToken } = useRecaptcha(props.recaptcha_site_key)

const toStr = (v: unknown) => (v == null ? '' : String(v))

const defaultAddress = (type: string): Address => ({
  street_no: '', street_name: '', street_type: '', unit_no: '',
  city: '', state: '', postcode: '', years_at_address: '', months_at_address: '', address_type: type,
})

const existingCurrent = props.existing_addresses.find(a => a.address_type === 'current')
const existingPrevious = props.existing_addresses.find(a => a.address_type === 'previous')

const currentAddress = reactive<Address>(
  existingCurrent
    ? { ...existingCurrent, years_at_address: toStr(existingCurrent.years_at_address), months_at_address: toStr(existingCurrent.months_at_address) }
    : defaultAddress('current')
)
const previousAddress = reactive<Address>(
  existingPrevious
    ? { ...existingPrevious, years_at_address: toStr(existingPrevious.years_at_address), months_at_address: toStr(existingPrevious.months_at_address) }
    : defaultAddress('previous')
)

const needsPreviousAddress = computed(() => {
  const years = parseInt(currentAddress.years_at_address)
  return !isNaN(years) && years < 3
})

const form = useForm({
  loan_type: props.prefill?.loan_type ?? '',
  purpose: props.prefill?.purpose ?? '',
  amount: props.prefill?.amount ?? '',
  loan_term: '',
  marital_status: '',
  dependents: '',
  monthly_income: '',
  annual_income: '',
  other_income: '',
  spouse_income: '',
  living_status: '',
  repayment_risk: '',
  repayment_risk_details: '',
})

const availablePurposes = computed(() => {
  if (!form.loan_type) return []
  return props.purposes[form.loan_type] ?? []
})

function onLoanTypeChange() {
  form.purpose = ''
}

function startFresh() {
  localStorage.removeItem('tlc_customer_id')
  localStorage.removeItem('tlc_application_id')
  router.visit(applyPagePath())
}

async function submit() {
  const recaptcha_token = await getToken('apply_step2')
  form.transform(data => ({
    ...data,
    repayment_risk: data.repayment_risk === 'yes',
    addresses: {
      current: currentAddress,
      previous: needsPreviousAddress.value ? previousAddress : undefined,
    },
    customer_id: props.customer_id,
    recaptcha_token,
  })).post(applyPageCreateApplicationPath())
}
</script>

<template>
  <Head title="Loan Details — Apply" />
  <LandingLayout>
    <div class="mb-6 flex items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold">
          <template v-if="customer_name">Hello, {{ customer_name }}!</template>
          <template v-else>Loan Details</template>
        </h1>
        <p class="text-muted-foreground mt-1 text-sm">Step 2 of 3 — Your loan requirements</p>
      </div>
      <button type="button" @click="startFresh" class="text-muted-foreground hover:text-foreground shrink-0 text-xs underline">
        Not you? Start fresh
      </button>
    </div>

    <form @submit.prevent="submit" novalidate class="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle class="text-base">Loan Information</CardTitle>
        </CardHeader>
        <CardContent class="space-y-4">
          <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <div class="grid gap-2">
              <Label for="loan_type">Loan Type <span class="text-destructive">*</span></Label>
              <Select v-model="form.loan_type" @update:model-value="onLoanTypeChange">
                <SelectTrigger id="loan_type" :class="['w-full', form.errors.loan_type && 'border-destructive focus-visible:ring-destructive']">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="t in props.loan_types" :key="t" :value="t">
                    {{ formatLabel(t) }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="form.errors.loan_type" />
            </div>
            <div class="grid gap-2">
              <Label for="purpose">Purpose <span class="text-destructive">*</span></Label>
              <Select v-model="form.purpose" :disabled="!form.loan_type">
                <SelectTrigger id="purpose" :class="['w-full', form.errors.purpose && 'border-destructive focus-visible:ring-destructive']">
                  <SelectValue placeholder="Select purpose" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="p in availablePurposes" :key="p" :value="p">
                    {{ formatLabel(p) }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="form.errors.purpose" />
            </div>
          </div>
          <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <div class="grid gap-2">
              <Label for="amount">Amount ($) <span class="text-destructive">*</span></Label>
              <Input
                id="amount"
                v-model="form.amount"
                name="amount"
                type="number"
                min="2000"
                :max="form.loan_type === 'vehicle' ? 150000 : 72000"
                step="500"
                placeholder="5000"
                :class="form.errors.amount && 'border-destructive focus-visible:ring-destructive'"
              />
              <InputError :messages="form.errors.amount" />
            </div>
            <div class="grid gap-2">
              <Label for="loan_term">Loan Term (months)</Label>
              <Select v-model="form.loan_term">
                <SelectTrigger id="loan_term" :class="['w-full', form.errors.loan_term && 'border-destructive focus-visible:ring-destructive']">
                  <SelectValue placeholder="Select term" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="t in loan_terms" :key="t" :value="String(t)">
                    {{ t }} months
                  </SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="form.errors.loan_term" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle class="text-base">Financial Profile</CardTitle>
        </CardHeader>
        <CardContent class="space-y-4">
          <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <div class="grid gap-2">
              <Label for="marital_status">Marital Status <span class="text-destructive">*</span></Label>
              <Select v-model="form.marital_status">
                <SelectTrigger id="marital_status" :class="['w-full', form.errors.marital_status && 'border-destructive focus-visible:ring-destructive']">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="s in props.marital_statuses" :key="s" :value="s">
                    {{ formatLabel(s) }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="form.errors.marital_status" />
            </div>
            <div class="grid gap-2">
              <Label for="dependents">Dependents <span class="text-destructive">*</span></Label>
              <Select v-model="form.dependents">
                <SelectTrigger id="dependents" :class="['w-full', form.errors.dependents && 'border-destructive focus-visible:ring-destructive']">
                  <SelectValue placeholder="Select dependents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="n in 10" :key="n - 1" :value="String(n - 1)">{{ n - 1 }}</SelectItem>
                </SelectContent>
              </Select>
              <InputError :messages="form.errors.dependents" />
            </div>
          </div>
          <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <div class="grid gap-2">
              <Label for="monthly_income">Monthly Income ($) <span class="text-destructive">*</span></Label>
              <Input id="monthly_income" v-model="form.monthly_income" name="monthly_income" type="number" min="0" :class="form.errors.monthly_income && 'border-destructive focus-visible:ring-destructive'" />
              <InputError :messages="form.errors.monthly_income" />
            </div>
            <div class="grid gap-2">
              <Label for="annual_income">Annual Income ($) <span class="text-destructive">*</span></Label>
              <Input id="annual_income" v-model="form.annual_income" name="annual_income" type="number" min="0" :class="form.errors.annual_income && 'border-destructive focus-visible:ring-destructive'" />
              <InputError :messages="form.errors.annual_income" />
            </div>
          </div>
          <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
            <div class="grid gap-2">
              <Label for="other_income">Other Income ($)</Label>
              <Input id="other_income" v-model="form.other_income" name="other_income" type="number" min="0" placeholder="Optional" />
            </div>
            <div class="grid gap-2">
              <Label for="spouse_income">Spouse Income ($)</Label>
              <Input id="spouse_income" v-model="form.spouse_income" name="spouse_income" type="number" min="0" placeholder="Optional" />
            </div>
          </div>
          <div class="grid gap-2">
            <Label for="living_status">Living Status <span class="text-destructive">*</span></Label>
            <Select v-model="form.living_status">
              <SelectTrigger id="living_status" :class="['w-full', form.errors.living_status && 'border-destructive focus-visible:ring-destructive']"><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>
                <SelectItem v-for="s in props.living_statuses" :key="s" :value="s">
                  {{ formatLabel(s) }}
                </SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="form.errors.living_status" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle class="text-base">Anticipated Changes</CardTitle>
        </CardHeader>
        <CardContent class="space-y-4">
          <div class="grid gap-2">
            <Label class="block text-sm font-medium leading-6 text-foreground/65">
              Do you anticipate any significant changes that might impact your ability to repay the loan?
              <span class="text-destructive">*</span>
            </Label>
            <RadioGroup v-model="form.repayment_risk" class="flex gap-6">
              <div class="flex items-center gap-2">
                <RadioGroupItem id="repayment_risk_no" value="no" />
                <Label for="repayment_risk_no" class="font-normal">No</Label>
              </div>
              <div class="flex items-center gap-2">
                <RadioGroupItem id="repayment_risk_yes" value="yes" />
                <Label for="repayment_risk_yes" class="font-normal">Yes</Label>
              </div>
            </RadioGroup>
            <InputError :messages="form.errors.repayment_risk" />
          </div>
          <div v-if="form.repayment_risk === 'yes'" class="grid gap-2">
            <Label class="block text-sm font-medium leading-6 text-foreground/65" for="repayment_risk_details">
              What changes do you anticipate? <span class="text-destructive">*</span>
            </Label>
            <Textarea
              id="repayment_risk_details"
              v-model="form.repayment_risk_details"
              maxlength="255"
              :class="form.errors.repayment_risk_details && 'border-destructive focus-visible:ring-destructive'"
            />
            <InputError :messages="form.errors.repayment_risk_details" />
          </div>
        </CardContent>
      </Card>

      <!-- Current Address -->
      <Card>
        <CardHeader><CardTitle class="text-base">Current Address</CardTitle></CardHeader>
        <CardContent>
          <AddressForm
            :model-value="currentAddress"
            :errors="form.errors"
            error-prefix="current_address_"
            prefix="current_"
            show-duration
            @update:model-value="Object.assign(currentAddress, $event)"
          />
        </CardContent>
      </Card>

      <!-- Previous Address (shown when < 3 years at current) -->
      <Card v-if="needsPreviousAddress">
        <CardHeader>
          <CardTitle class="text-base">Previous Address</CardTitle>
          <p class="text-muted-foreground mt-1 text-sm">Required — less than 3 years at current address</p>
        </CardHeader>
        <CardContent>
          <AddressForm
            :model-value="previousAddress"
            :errors="form.errors"
            error-prefix="previous_address_"
            prefix="previous_"
            show-duration
            @update:model-value="Object.assign(previousAddress, $event)"
          />
        </CardContent>
      </Card>

      <Button type="submit" class="w-full" :disabled="form.processing">
        Continue to Financial Details
      </Button>
    </form>
  </LandingLayout>
</template>
